import { useEffect, useRef, useState } from 'react'
import { useT } from '../i18n/useT'

const stepMeta = [
  {
    tint: 'bg-teal-50',
    stroke: '#0d9488',
    icon: (
      <>
        <circle cx="6" cy="6" r="2.4" />
        <circle cx="18" cy="6" r="2.4" />
        <circle cx="12" cy="18" r="2.4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.2 7.2l2.6 8.4M15.8 7.2l-2.6 8.4M8.4 6h7.2" />
      </>
    ),
  },
  {
    tint: 'bg-emerald-50',
    stroke: '#059669',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.2 12l2 2 3.8-4" />
      </>
    ),
  },
  {
    tint: 'bg-sky-50',
    stroke: '#0284c7',
    icon: (
      <>
        <rect x="3" y="7" width="18" height="11" rx="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 11h18M7 15h4" />
      </>
    ),
  },
]

// Sequencing (ms) — card reveal, then connector draw, staggered.
const stepDelay = [0, 900, 1800]
const connDelay = [450, 1350]
const checkDelay = [350, 1250, 2150]

export default function Workflow() {
  const t = useT()
  const ref = useRef<HTMLDivElement>(null)
  const [on, setOn] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setOn(true)
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setOn(true)
          obs.disconnect()
        }
      },
      { threshold: 0.35 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={ref} className={on ? 'wf-on' : ''}>
      <div className="relative grid grid-cols-3 gap-16">
        {/* connectors, absolutely positioned behind the cards */}
        <div className="absolute top-10 left-0 right-0 hidden lg:block" aria-hidden>
          <div className="mx-[calc(16.67%+3rem)] mr-[calc(16.67%+3rem)] relative h-0.5">
            <div
              className="wf-connector absolute left-0 top-0 h-0.5 w-[calc(50%+1.5rem)] -ml-6 bg-gradient-to-r from-brand-300 to-brand-500 rounded"
              style={{ transitionDelay: `${connDelay[0]}ms` }}
            />
            <div
              className="wf-connector absolute right-0 top-0 h-0.5 w-[calc(50%+1.5rem)] -mr-6 bg-gradient-to-r from-brand-500 to-brand-300 rounded"
              style={{ transitionDelay: `${connDelay[1]}ms`, transformOrigin: 'left center' }}
            />
          </div>
        </div>

        {t.home.workflow.steps.map((s, i) => (
          <div
            key={s.title}
            className="wf-step relative bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_-12px_rgb(15_23_42/0.12)] p-8"
            style={{ transitionDelay: `${stepDelay[i]}ms` }}
          >
            <div className="relative w-20 h-20 mx-auto -mt-16 mb-6">
              <div className={`w-20 h-20 rounded-2xl ${stepMeta[i].tint} border border-slate-100 grid place-items-center shadow-[0_8px_20px_-8px_rgb(15_23_42/0.15)]`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={stepMeta[i].stroke} strokeWidth={1.8} className="w-9 h-9">
                  {stepMeta[i].icon}
                </svg>
              </div>
              <span
                className="wf-check absolute -top-2 -right-2 w-7 h-7 rounded-full bg-brand-100 border-2 border-white grid place-items-center"
                style={{ transitionDelay: `${checkDelay[i]}ms` }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth={3} className="w-3.5 h-3.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4 10-10" />
                </svg>
              </span>
            </div>
            <p className="text-sm font-semibold text-brand-700 mb-1.5 text-center">
              {t.home.workflow.step} {i + 1}
            </p>
            <h3 className="text-xl font-bold text-center mb-3">{s.title}</h3>
            <p className="text-[15px] leading-relaxed text-ink-mute text-center">
              {s.body}
            </p>
          </div>
        ))}
      </div>

      <p
        className="wf-check text-center mt-12 text-[15px] font-medium text-ink-mute"
        style={{ transitionDelay: '2500ms' }}
      >
        {t.home.workflow.foot1}
        <span className="text-brand-700 font-semibold">{t.home.workflow.foot2}</span>
      </p>
    </div>
  )
}
